import type { ConfigType, Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import calendar from 'dayjs/plugin/calendar'
import duration from 'dayjs/plugin/duration'
import relativeTime from 'dayjs/plugin/relativeTime'
import { format } from 'quasar'
import { i18n } from '../Utils/i18n'

// Initialize DayJS plugins locally if not done globally in main.ts
dayjs.extend(relativeTime)
dayjs.extend(duration)
dayjs.extend(calendar)
dayjs.locale('fr')

const { capitalize } = format

/**
 * Date and Time manipulation utilities.
 * Removes dependency on humanize-duration in favor of Day.js plugins and native math.
 *
 * @public
 */
export default function useDate() {
  // --------------------------------------------------------------------------
  // Core Helpers
  // --------------------------------------------------------------------------

  /**
   * Internal helper to normalize any date input into a valid Dayjs object.
   */
  const parseDate = (v: ConfigType | number): Dayjs => {
    if (!v) return dayjs()
    // Handle 10-digit Unix timestamp (seconds)
    if (typeof v === 'number' && v.toString().length === 10) {
      return dayjs.unix(v)
    }
    // Handle string timestamp
    if (typeof v === 'string' && /^\d{10}$/.test(v)) {
      return dayjs.unix(parseInt(v, 10))
    }
    return dayjs(v)
  }

  // --------------------------------------------------------------------------
  // Modern API
  // --------------------------------------------------------------------------

  /**
   * Formats a duration in seconds, for example "1h 02 min".
   * Completely replaces humanize-duration for work logs/time spent.
   *
   * @param seconds - Duration in seconds
   * @param displaySeconds - Whether to include remaining seconds (e.g. "1h 02 min 30 sec")
   * @returns Formatted string
   * @public
   */
  const toHumanDuration = (seconds: number, displaySeconds = false): string => {
    if (!seconds || seconds <= 0) return '0 sec'

    const d = dayjs.duration(seconds, 'seconds')

    const h = Math.floor(d.asHours())
    const m = d.minutes()
    const s = d.seconds()

    if (h > 0) {
      return `${h}h ${m.toString().padStart(2, '0')} min${displaySeconds && s > 0 ? ` ${s} sec` : ''}`
    }
    if (m > 0) {
      return `${m} min${displaySeconds && s > 0 ? ` ${s} sec` : ''}`
    }
    if (displaySeconds || s > 0) {
      return `${s} sec`
    }
    return '0 sec'
  }

  /**
   * Calculates the relative time from now (e.g., "il y a 3 jours").
   * Uses Day.js RelativeTime plugin.
   *
   * @param date - The target date
   * @param withoutSuffix - If true, returns "3 jours" instead of "il y a 3 jours"
   * @returns Relative time string
   * @public
   */
  const getRelativeTime = (date: ConfigType, withoutSuffix = false): string => {
    const d = parseDate(date)
    return d.locale(String(i18n.locale.value).startsWith('en') ? 'en' : 'fr').fromNow(withoutSuffix)
  }

  /**
   * Calculates the duration between two dates.
   *
   * @param start - Start date
   * @param end - End date
   * @param displaySeconds - Whether to show seconds in the output
   * @returns Formatted duration string
   * @public
   */
  const getDurationBetween = (start: ConfigType, end: ConfigType, displaySeconds = false): string => {
    const s = parseDate(start)
    const e = parseDate(end)
    const diffSeconds = Math.max(0, e.diff(s, 'second'))
    return toHumanDuration(diffSeconds, displaySeconds)
  }

  /**
   * Formats a date string for backend transmission (ISO 8601 Date).
   * @public
   */
  const toISODate = (date: ConfigType): string => {
    return parseDate(date).format('YYYY-MM-DD')
  }

  /**
   * Formats a date for human reading with optional time.
   * @public
   */
  const toReadableDate = (date: ConfigType = null, includeTime = false, includeDayName = false): string => {
    // Check for null/undefined/0 before parsing
    if (date === null || date === undefined || (typeof date === 'number' && date === 0)) {
      return 'Non spécifié'
    }
    const d = parseDate(date)
    if (!d.isValid()) return 'Non spécifié'

    let fmt = 'YYYY-MM-DD'
    if (includeTime) fmt += ' HH:mm'
    if (includeDayName) fmt = `dddd ${fmt}`

    return capitalize(d.format(fmt))
  }

  /**
   * Extracts the time portion of a date.
   * @public
   */
  const toReadableTime = (date: ConfigType, includeSeconds = false): string => {
    if (!date) return '-'
    const d = parseDate(date)
    if (!d.isValid()) return '-'
    return d.format(includeSeconds ? 'HH:mm:ss' : 'HH:mm')
  }

  /**
   * Generates a "Calendar" string (relative description).
   * @public
   */
  const toCalendarString = (date: ConfigType = null, includeTime = true): string => {
    const d = date ? parseDate(date) : dayjs()
    if (!d.isValid() || (typeof date === 'number' && date === 0)) return 'Non spécifié'

    // Day.js calendar allows a callback or object
    return capitalize(
      d.calendar(null, {
        sameDay: includeTime ? "[Aujourd'hui à] HH:mm" : "[Aujourd'hui]",
        nextDay: includeTime ? '[Demain à] HH:mm' : '[Demain]',
        nextWeek: includeTime ? 'dddd [prochain à] HH:mm' : 'dddd [prochain]',
        lastDay: includeTime ? '[Hier à] HH:mm' : '[Hier]',
        lastWeek: includeTime ? 'dddd [dernier à] HH:mm' : 'dddd [dernier]',
        sameElse: includeTime ? 'dddd D MMMM YYYY [à] HH:mm' : 'dddd D MMMM YYYY',
      }),
    )
  }

  /**
   * Gets the day name (e.g., "Lundi")
   * @public
   */
  const getDayName = (input: string | number): string => {
    if (typeof input === 'string' && input.length > 1 && input.includes('-')) {
      return dayjs(`2023-${input}`).format('DD MMMM')
    }
    const dayIndex = typeof input === 'string' ? parseInt(input, 10) : input
    const d = dayjs().day(dayIndex)
    return capitalize(d.format('dddd'))
  }

  // --------------------------------------------------------------------------
  // Deprecated / Aliases (For backward compatibility)
  // --------------------------------------------------------------------------

  /** @deprecated Use `toReadableDate` */
  const formatDate = (v: ConfigType = null, time = false, day = false) => toReadableDate(v, time, day)

  /** @deprecated Use `toReadableTime` */
  const formatTime = (v: string, seconds = false) => toReadableTime(v, seconds)

  /** @deprecated Use `toReadableTime` */
  const dateToTime = (date: ConfigType) => toReadableTime(date, true)

  /** @deprecated Use `toReadableTime` */
  const dateToTimeFromDate = (date: string) => toReadableTime(date, true)

  /** @deprecated Use `toISODate` */
  const formatForServer = (date: string) => toISODate(date)

  /** @deprecated Use `getDurationBetween` or `toHumanDuration` */
  const formatDuration = (debut: string, fin: string, output: 'd' | 's' = 'd', convert = true): number | string => {
    const diff = dayjs(fin).diff(debut, output === 'd' ? 'day' : 'second', true)
    const val = output === 'd' ? diff + 1 : diff
    if (output === 's' && convert) return toHumanDuration(Number(val), true)
    return val
  }

  /** @deprecated Use `toHumanDuration` */
  const humanize = (v: number) => toHumanDuration(v)

  /** @deprecated Use `getRelativeTime(v, true)` */
  const daysAgo = (v: ConfigType) => getRelativeTime(v, true)

  /** @deprecated Use `toCalendarString` */
  const dateToDay = (v: ConfigType = null, time = true) => toCalendarString(v, time)

  /** @deprecated Use `getDurationBetween` */
  const formatTimeSpent = (start: string, end: string, displaySeconds = false) => getDurationBetween(start, end, displaySeconds)

  /** @deprecated Use `toHumanDuration` */
  const formatMinutes = (mins: number) => toHumanDuration(mins * 60)

  /** @deprecated Use `getDayName` */
  const getFormattedDayName = (dm: string) => getDayName(dm)

  /** @deprecated Use `dayjs(v).isSame(dayjs(), 'day')` */
  const isToday = (dateStr: string) => parseDate(dateStr).isSame(dayjs(), 'day')

  /** @deprecated Use `dayjs(v).format('dddd')` */
  const formatDayName = (dateStr: string) => parseDate(dateStr).format('dddd')

  /** @deprecated Use `dayjs(v).format('D MMM')` */
  const formatDayNumber = (dateStr: string) => parseDate(dateStr).format('D MMM')

  /** @deprecated Use `getRelativeTime` */
  const timeAgo = (v: ConfigType, dateStart: ConfigType = null): string => {
    // Note: humanize-duration handled precise math between two arbitrary dates slightly differently
    // than Dayjs relative time, but for "Time Ago" logic, Dayjs from() is standard.
    const start = dateStart ? parseDate(dateStart) : dayjs()
    const target = parseDate(v)
    return target.from(start)
  }

  return {
    // New API
    toISODate,
    toReadableDate,
    toReadableTime,
    toCalendarString,
    toHumanDuration,
    getRelativeTime,
    getDurationBetween,
    getDayName,

    // Deprecated
    formatDate,
    formatTime,
    getFormattedDayName,
    dateToTime,
    dateToTimeFromDate,
    formatForServer,
    formatDuration,
    humanize,
    dateToDay,
    timeAgo,
    formatTimeSpent,
    formatMinutes,
    isToday,
    formatDayName,
    formatDayNumber,
    daysAgo,
  }
}
